import React, { useEffect, useState } from "react";
import { Calendar } from "@/components/ui/calendar/Calendar";
import { Option } from "@/components/multi-select-filter/SelectionFilter";
import SelectionFilter from "@/components/multi-select-filter/SelectionFilter";
import { DateBefore, Matcher } from "react-day-picker";
import { IShift } from "@/models/shift/Shift";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import ShiftService from "@/lib/services/shift/ShiftService";

// target date: 1 Oct 2025
// date when min borrowing advance day changes from 1 to 2 days
// TODO: remove after target date
const currDate = new Date();
const targetDate = new Date(2025, 9, 1);

targetDate.setHours(0, 0, 0, 0);

const minBorrowAdvanceDays = currDate >= targetDate ? 2 : 1;
const minBorrowDate = new Date();
minBorrowDate.setDate(minBorrowDate.getDate() + minBorrowAdvanceDays);

type Props = {
  shiftData: IShift[];
  loading: boolean;
  selectedShifts: Option[] | Option | null;
  onChangeShifts: (options: Option[] | Option | null) => void;
  date: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
  title: string;
  FilterComponent: React.ReactElement;
  checkOutDate?: Date;
};

const DateSelect = (props: Props) => {
  const [disabledDays, setDisabledDays] = useState<Matcher[]>([
    { before: minBorrowDate },
    { dayOfWeek: [0] },
  ]);

  useEffect(() => {
    let ignore = false;
    const fetchHolidays = async () => {
      try {
        const holidays = await ShiftService.getAllHoliday(
          new Date().getFullYear().toString()
        );

        if(ignore) return;
        setDisabledDays((prevDays) => [...prevDays, ...holidays]);
      } catch (error) {
        console.error("Error fetching holiday data:", error);
      }
    };

    fetchHolidays();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (props.checkOutDate) {
      setDisabledDays((prevDays) => [
        ...prevDays,
        { before: props.checkOutDate } as DateBefore,
      ]);
    }
  }, [props.checkOutDate]);

  return (
    <div className="w-full min-h-72 flex flex-col lg:flex-row gap-4">
      <div className="flex flex-col">
        <h3 className="pb-1">
          <div className="text-base md:text-lg font-semibold">
            {props.title}
          </div>
          <div className="text-gray-400 text-sm">
            You can only select dates at least {minBorrowAdvanceDays} days in
            advance.
          </div>
        </h3>
        <Calendar
          className="self-center"
          mode="single"
          captionLayout="dropdown-buttons"
          selected={props.date}
          onSelect={(date) => props.onSelectDate(date)}
          disabled={disabledDays}
          fromYear={new Date().getFullYear()}
          toYear={new Date().getFullYear() + 2}
        />
      </div>
      <div className="flex-1">
        <h3 className="pb-1">
          <div className="text-base md:text-lg font-semibold">Select Shift</div>
          <div className="text-gray-400 text-sm">
            {props?.FilterComponent?.type === SelectionFilter
              ? "You can select multiple shifts"
              : "You can select one shift"}
          </div>
        </h3>

        <h4
          className={`text-lg ${
            props.date === undefined
              ? "opacity-100"
              : "opacity-0 fixed top-[-1000px]"
          } transition-opacity ease-out duration-200`}
        >
          Please select a date before selecting shift.
        </h4>
        {props.loading ? (
          <LoadingIndicator loading={props.loading} />
        ) : props.shiftData.length < 1 ? (
          <div>No shifts available</div>
        ) : (
          <div
            className={`${
              props.date === undefined ? "opacity-0" : "opacity-100"
            } transition-opacity ease-in duration-200`}
          >
            {props.FilterComponent}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateSelect;
